import React, { useState } from 'react';
import { useData } from '../../store/DataContext';
import { Button } from '../../components/ui/Button';
import { CheckCircleIcon, ClipboardListIcon, FileTextIcon, ScrollTextIcon } from 'lucide-react';
import { Encounter } from '../../types';
import { PrescriptionFormPreview } from './PrescriptionFormPreview';
type Props = {
  onBack?: () => void;
};
export function PharmacyDispensing({
  onBack
}: Props) {
  const {
    encounters,
    patients,
    currentUser,
    updateEncounter,
    dispenseMedicine
  } = useData();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [statuses, setStatuses] = useState<Record<string, 'Dispensed' | 'Prescribed'>>({});
  const [message, setMessage] = useState('');
  const [showPrescriptionForm, setShowPrescriptionForm] = useState(false);
  const selected = selectedId ? encounters.find(encounter => encounter.id === selectedId) || null : null;
  const pending = encounters.filter(encounter => encounter.stage === 'PHARMACY');
  const patient = selected ? patients.find(item => item.id === selected.patientId) : undefined;
  const selectEncounter = (encounter: Encounter) => {
    setSelectedId(encounter.id);
    setStatuses(encounter.prescriptions.reduce<Record<string, 'Dispensed' | 'Prescribed'>>((nextStatuses, prescription) => {
      if (prescription.status !== 'Pending') {
        nextStatuses[prescription.id] = prescription.status === 'Dispensed' ? 'Dispensed' : 'Prescribed';
      }
      return nextStatuses;
    }, {}));
    setMessage('');
    setShowPrescriptionForm(false);
  };
  const markDispensed = async (prescriptionId: string, drugName: string) => {
    if (!selected) return;
    const result = await dispenseMedicine({
      drugName,
      encounterId: selected.id,
      patientId: selected.patientId,
      prescriptionId
    });
    if (!result.ok) {
      setMessage(`${result.error || 'Unable to dispense this medicine.'} Mark it as PRESCRIBED to keep it on the record without deducting stock.`);
      return;
    }
    setStatuses(previous => ({
      ...previous,
      [prescriptionId]: 'Dispensed'
    }));
    setMessage('DISPENSED — one unit was deducted using FEFO from an eligible unexpired batch and recorded in stock history.');
  };
  const markPrescribed = (prescriptionId: string) => {
    setStatuses(previous => ({
      ...previous,
      [prescriptionId]: 'Prescribed'
    }));
    setMessage('PRESCRIBED — the item stays on the prescription record and no stock is deducted.');
  };
  const complete = () => {
    if (!selected || !currentUser) return;
    if (!selected.prescriptions.every(prescription => statuses[prescription.id])) {
      setMessage('Mark each item as DISPENSED or PRESCRIBED before completing.');
      return;
    }
    updateEncounter(selected.id, {
      prescriptions: selected.prescriptions.map(prescription => {
        const status = statuses[prescription.id] || prescription.status;
        return {
          ...prescription,
          status,
          ...(status === 'Dispensed' ? {
            dispensedAt: new Date().toISOString(),
            dispensedBy: currentUser.id,
            pharmacistName: currentUser.fullName
          } : {})
        };
      }),
      stage: 'CLINICIAN'
    });
    setSelectedId(null);
    setStatuses({});
    setShowPrescriptionForm(false);
    setMessage('Dispensing recorded and patient file returned to the clinician.');
  };
  if (selected && patient) return <div className="space-y-5" data-id="element-5242" data-mp="2xvojfu5wk8m6">
        <div className="flex flex-col gap-3 border-b border-slate-200 pb-5 sm:flex-row sm:items-center sm:justify-between" data-id="element-5243" data-mp="2xun2xa64atx2">
          <div data-id="element-5244" data-mp="i2phjvlpj2ao">
            <p className="text-sm font-semibold text-pink-600" data-id="element-5245" data-mp="i6n12ic75vv3">Dispensing</p>
            <h2 className="mt-1 text-xl font-bold text-slate-950" data-id="element-5246" data-mp="d4xogy9bta4w">
              {patient.firstName} {patient.lastName}
            </h2>
            <p className="mt-1 text-sm text-slate-500" data-id="element-5247" data-mp="2nst7sq1974zr">
              {patient.bmcFileNumber} · Stock is only deducted for items marked
              DISPENSED.
            </p>
          </div>
          <div className="flex flex-wrap gap-2" data-id="element-5248" data-mp="2rwr4yv70q5q6">
            <Button variant="outline" onClick={() => {
          setSelectedId(null);
          setStatuses({});
          setMessage('');
          setShowPrescriptionForm(false);
        }} data-id="element-5249" data-mp="2wukdvvzphdu5">
              Back to queue
            </Button>
            <Button variant="outline" onClick={() => setShowPrescriptionForm(true)} className="border-teal-200 text-teal-800 hover:bg-teal-50" data-id="element-5250" data-mp="3bjowvfebrm3b">
              <FileTextIcon className="mr-2 h-4 w-4" data-id="element-5251" data-mp="3kkep694dmuca" /> Generate prescription
              form
            </Button>
            <Button onClick={complete} className="bg-teal-600 hover:bg-teal-700" data-id="element-5252" data-mp="3pig3yhurs7hy">
              <CheckCircleIcon className="mr-2 h-4 w-4" data-id="element-5253" data-mp="3un0f19rq9t55" /> Complete dispensing
            </Button>
          </div>
        </div>
        {message && <p role="status" className="rounded-md bg-blue-50 px-3 py-2 text-sm text-blue-800" data-id="element-5254" data-mp="3pop40vpp5clc">
            {message}
          </p>}
        <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm" data-id="element-5255" data-mp="m7qfo9f83212">
          <div className="border-b border-slate-100 px-5 py-4" data-id="element-5256" data-mp="h7m1wr5p8lej">
            <h3 className="font-bold text-slate-900" data-id="element-5257" data-mp="hagk0c18pu4s">Prescribed medicines</h3>
            <p className="mt-1 text-sm text-slate-500" data-id="element-5258" data-mp="cfkrzbw076ro">
              <span className="font-semibold text-slate-700" data-id="element-5259" data-mp="8glprz1npo3x">DISPENSED</span> —
              available in inventory and supplied to the patient (one unit
              deducted by FEFO).{' '}
              <span className="font-semibold text-slate-700" data-id="element-5260" data-mp="106gcrp21l1pf">PRESCRIBED</span> —
              not available in inventory, so nothing is deducted and the
              prescription stays on record.
            </p>
          </div>
          <div className="divide-y divide-slate-100" data-id="element-5261" data-mp="2zclqz4p0p87d">
            {selected.prescriptions.map(prescription => <article key={prescription.id} className="flex flex-col gap-4 px-5 py-4 lg:flex-row lg:items-center lg:justify-between" data-id="element-5262" data-mp="2koqvxhnjerr4">
                <div data-id="element-5263" data-mp="2bnjc24lrptgk">
                  <h4 className="font-semibold text-slate-900" data-id="element-5264" data-mp="2bn18emws0cym">
                    {prescription.drugName}
                  </h4>
                  <p className="mt-1 text-sm text-slate-500" data-id="element-5265" data-mp="21qellskw3bty">
                    {prescription.dose} · {prescription.route} ·{' '}
                    {prescription.frequency} · {prescription.days} days
                  </p>
                </div>
                <div className="flex items-center gap-2" data-id="element-5266" data-mp="33g59avevg">
                  <span className={`mr-1 rounded-full px-2 py-1 text-xs font-bold uppercase tracking-wide ${statuses[prescription.id] === 'Dispensed' ? 'bg-emerald-50 text-emerald-700' : statuses[prescription.id] === 'Prescribed' ? 'bg-amber-50 text-amber-800' : 'bg-slate-100 text-slate-600'}`} data-id="element-5267" data-mp="4xmnjlsoilvb">
                    {statuses[prescription.id] || 'Awaiting action'}
                  </span>
                  <Button size="sm" disabled={Boolean(statuses[prescription.id])} onClick={() => markDispensed(prescription.id, prescription.drugName)} className="bg-teal-600 hover:bg-teal-700" data-id="element-5268" data-mp="1ue8c9cxfs09f">
                    <CheckCircleIcon className="mr-1.5 h-4 w-4" data-id="element-5269" data-mp="2i2i1ypxrt85u" /> Dispensed
                  </Button>
                  <Button size="sm" variant="outline" disabled={Boolean(statuses[prescription.id])} onClick={() => markPrescribed(prescription.id)} className="border-amber-300 text-amber-800 hover:bg-amber-50" data-id="element-5270" data-mp="2d3or759vbv9d">
                    <ScrollTextIcon className="mr-1.5 h-4 w-4" data-id="element-5271" data-mp="1ww16lspolnt" /> Prescribed
                  </Button>
                </div>
              </article>)}
          </div>
        </section>
        {showPrescriptionForm && <PrescriptionFormPreview encounter={selected} patient={patient} pharmacist={currentUser} onClose={() => setShowPrescriptionForm(false)} data-id="element-5272" data-mp="blw4uii5s58c" />}
      </div>;
  return <div className="space-y-5" data-id="element-5273" data-mp="3f5qzphijqcso">
      <div className="flex flex-col gap-3 border-b border-slate-200 pb-5 sm:flex-row sm:items-end sm:justify-between" data-id="element-5274" data-mp="3k2r8i06s5tnh">
        <div data-id="element-5275" data-mp="3p2veuxdz57zz">
          <p className="text-sm font-semibold text-pink-600" data-id="element-5276" data-mp="3p3ony50eo9la">Pharmacy desk</p>
          <h2 className="mt-1 text-xl font-bold text-slate-950" data-id="element-5277" data-mp="zbqhayykfrs2">
            Dispensing queue
          </h2>
          <p className="mt-1 text-sm text-slate-500" data-id="element-5278" data-mp="14ajroiik32o0">
            Patient prescriptions waiting for pharmacy action.
          </p>
        </div>
        {onBack && <Button variant="outline" onClick={onBack} data-id="element-5279" data-mp="2juaqgf53vozg">
            Back to overview
          </Button>}
      </div>
      <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm" data-id="element-5280" data-mp="2f32h1j3s03ma">
        <div className="divide-y divide-slate-100" data-id="element-5281" data-mp="2a3znovx5rlu5">
          {pending.map(encounter => {
          const queuePatient = patients.find(item => item.id === encounter.patientId);
          if (!queuePatient) return null;
          return <article key={encounter.id} className="flex flex-col gap-4 px-5 py-4 sm:flex-row sm:items-center sm:justify-between" data-id="element-5282" data-mp="1w4q7vxcv6tqp">
                <div className="flex items-center gap-3" data-id="element-5283" data-mp="3qj9adzxeox8q">
                  <div className="rounded-lg bg-teal-50 p-2.5 text-teal-700" data-id="element-5284" data-mp="3qir56dsrn8oo">
                    <ClipboardListIcon className="h-5 w-5" data-id="element-5285" data-mp="3ll7mn1p1c9qx" />
                  </div>
                  <div data-id="element-5286" data-mp="19iczr5gts72g">
                    <p className="font-semibold text-slate-900" data-id="element-5287" data-mp="19hl4jvr54bcp">
                      {queuePatient.firstName} {queuePatient.lastName}
                    </p>
                    <p className="mt-0.5 text-sm text-slate-500" data-id="element-5288" data-mp="1ikcj44fbbn8k">
                      {queuePatient.bmcFileNumber} ·{' '}
                      {encounter.prescriptions.length} prescribed items
                    </p>
                  </div>
                </div>
                <Button size="sm" onClick={() => selectEncounter(encounter)} className="bg-teal-600 hover:bg-teal-700" data-id="element-5289" data-mp="qqka9l0yt3zh">
                  Review prescription
                </Button>
              </article>;
        })}
          {!pending.length && <div className="px-5 py-14 text-center" data-id="element-5290" data-mp="745q5oplgvmn">
              <ClipboardListIcon className="mx-auto h-8 w-8 text-slate-300" data-id="element-5291" data-mp="72uq9zz9s80t" />
              <p className="mt-3 text-sm text-slate-500" data-id="element-5292" data-mp="32aryt2ymyd3">
                No prescriptions are waiting for pharmacy.
              </p>
            </div>}
        </div>
      </section>
    </div>;
}